import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef, inject, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { firstValueFrom } from 'rxjs'

declare const microsoftTeams: any

type PanelStatus =
  | 'LOADING'
  | 'NO_CALL'
  | 'NO_ACCOUNT'
  | 'NOT_A_PARTICIPANT'
  | 'SESSION_INVALID'
  | 'NOT_ENABLED'
  | 'SCHEDULED'
  | 'CONNECTING'
  | 'RECORDING'
  | 'PAUSED'
  | 'HANGING_UP'
  | 'ENDED'

const ACCESS_REASON_TO_STATUS: Partial<Record<string, PanelStatus>> = {
  NO_ACCOUNT: 'NO_ACCOUNT',
  NOT_A_PARTICIPANT: 'NOT_A_PARTICIPANT',
  SESSION_INVALID: 'SESSION_INVALID',
}

type CallInfo = {
  callId: number
  accessKey: string
  status: PanelStatus
  callUrl: string | null
  startedAt: string | null
  scheduledEndAt: string | null
}

@Component({
  selector: 'app-teams-panel',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './teams-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './teams-panel.component.scss',
})
export class TeamsPanelComponent implements OnInit, OnDestroy {
  panelStatus: PanelStatus = 'LOADING'
  callInfo: CallInfo | null = null
  elapsedTime = '00:00'
  remainingTime: string | null = null
  isOvertime = false
  actionInProgress = false
  errorMessage: string | null = null
  isReconnecting = false

  private timerInterval: ReturnType<typeof setInterval> | null = null
  private pollInterval: ReturnType<typeof setInterval> | null = null
  private resolveRetryTimeout: ReturnType<typeof setTimeout> | null = null
  private startTime = Date.now()
  private clockOffsetMs = 0
  private threadId: string | null = null
  private pollFailureCount = 0
  private readonly reconnectAfterFailures = 3

  // After a resend, the old session stays ENDED until the bot-manager spins up the new one.
  // While awaiting, keep polling instead of tearing down on ENDED so the panel flips back to
  // the live view on its own once the replacement assistant joins.
  private awaitingResend = false
  private resendWaitUntil = 0
  private readonly resendWaitMs = 180000

  private readonly apiBaseUrl = '/api/bot'
  private ssoToken: string | null = null

  private mapSessionStatus(status: string): PanelStatus {
    if (status === 'CONNECTING' || status === 'WAITING_TO_BE_ADMITTED' || status === 'QUEUED') {
      return 'CONNECTING'
    }
    if (status === 'PAUSED') {
      return 'PAUSED'
    }
    if (status === 'HANGING_UP') {
      return 'HANGING_UP'
    }
    if (status === 'ENDED' || status === 'ERRORED') {
      return 'ENDED'
    }
    return 'RECORDING'
  }
  private readonly http = inject(HttpClient)
  private readonly translate = inject(TranslateService)

  constructor(
    private readonly ngZone: NgZone,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadTeamsSdk()
  }

  ngOnDestroy() {
    this.clearTimers()
  }

  private clearTimers() {
    this.stopPolling()
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
    if (this.resolveRetryTimeout !== null) {
      clearTimeout(this.resolveRetryTimeout)
      this.resolveRetryTimeout = null
    }
  }

  private scheduleResolveRetry(threadId: string) {
    if (this.resolveRetryTimeout !== null) {
      clearTimeout(this.resolveRetryTimeout)
    }
    this.resolveRetryTimeout = setTimeout(() => {
      this.resolveRetryTimeout = null
      this.resolveCall(threadId)
    }, 5000)
  }

  isPausing = false
  isResuming = false
  isDisconnecting = false

  canExtend = false
  isExtending = false

  canResend = false
  showResendConfirm = false
  isResending = false
  resendError: string | null = null
  resendSuccess = false

  async pause() {
    if (this.callInfo === null || this.actionInProgress) {
      return
    }
    this.actionInProgress = true
    this.isPausing = true
    this.errorMessage = null

    try {
      await this.sendPanelRequest('POST', `${this.callInfo.callId}/pause`)
    } catch {
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_PAUSE')
      this.actionInProgress = false
      this.isPausing = false
    }
  }

  async resume() {
    if (this.callInfo === null || this.actionInProgress) {
      return
    }
    this.actionInProgress = true
    this.isResuming = true
    this.errorMessage = null

    try {
      await this.sendPanelRequest('POST', `${this.callInfo.callId}/resume`)
    } catch {
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_RESUME')
      this.actionInProgress = false
      this.isResuming = false
    }
  }

  showDisconnectConfirm = false

  requestDisconnect() {
    this.showDisconnectConfirm = true
  }

  cancelDisconnect() {
    this.showDisconnectConfirm = false
  }

  async confirmDisconnect() {
    this.showDisconnectConfirm = false
    if (this.callInfo === null || this.actionInProgress) {
      return
    }
    this.actionInProgress = true
    this.isDisconnecting = true
    this.errorMessage = null

    try {
      await this.sendPanelRequest('POST', `${this.callInfo.callId}/hang-up`)
    } catch {
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_DISCONNECT')
      this.actionInProgress = false
      this.isDisconnecting = false
    }
  }

  async extend() {
    if (this.callInfo === null || this.actionInProgress || this.isExtending) {
      return
    }
    this.isExtending = true
    this.errorMessage = null

    try {
      await this.sendPanelRequest('POST', `${this.callInfo.callId}/extend`)
      this.isExtending = false
      this.canExtend = false
    } catch {
      this.isExtending = false
      this.errorMessage = this.translate.instant('TEAMS_PANEL.ERROR_EXTEND')
    }
    this.changeDetector.detectChanges()
  }

  requestResend() {
    this.showResendConfirm = true
  }

  cancelResend() {
    this.showResendConfirm = false
  }

  async confirmResend() {
    this.showResendConfirm = false
    if (this.callInfo === null || this.isResending) {
      return
    }
    this.isResending = true
    this.resendError = null

    try {
      await this.sendPanelRequest('POST', `${this.callInfo.callId}/resend-bot`)
      this.isResending = false
      this.resendSuccess = true
      this.awaitingResend = true
      this.resendWaitUntil = Date.now() + this.resendWaitMs
      this.startTimer()
      this.startPolling()
      this.pollStatus()
    } catch {
      this.isResending = false
      this.resendError = this.translate.instant('TEAMS_PANEL.ERROR_RESEND')
    }
    this.changeDetector.detectChanges()
  }

  private loadTeamsSdk() {
    if (typeof microsoftTeams !== 'undefined') {
      this.initializeTeams()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://res.cdn.office.net/teams-js/2.24.0/js/MicrosoftTeams.min.js'
    script.onload = () => {
      this.initializeTeams()
    }
    script.onerror = () => {
      this.ngZone.run(() => {
        this.panelStatus = 'NO_CALL'
        this.changeDetector.detectChanges()
      })
    }
    document.head.appendChild(script)
  }

  private async fetchSsoToken(): Promise<string | null> {
    try {
      return await microsoftTeams.authentication.getAuthToken()
    } catch (error) {
      console.warn('Failed to get Teams SSO token', error)
      return null
    }
  }

  private async initializeTeams() {
    try {
      const initTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Teams SDK timeout')), 5000))
      await Promise.race([microsoftTeams.app.initialize(), initTimeout])

      this.ssoToken = await this.fetchSsoToken()

      const context = await microsoftTeams.app.getContext()
      const threadId = context?.chat?.id ?? null
      this.threadId = threadId

      if (threadId === null) {
        this.ngZone.run(() => {
          this.panelStatus = 'NO_CALL'
          this.changeDetector.detectChanges()
        })
        return
      }

      await this.resolveCall(threadId)
    } catch {
      this.ngZone.run(() => {
        this.panelStatus = 'NO_CALL'
        this.changeDetector.detectChanges()
      })
    }
  }

  private async resolveCall(threadId: string, tokenRefreshed = false) {
    try {
      const headers: Record<string, string> = {}
      if (this.ssoToken !== null) {
        headers['Authorization'] = `Bearer ${this.ssoToken}`
      }
      const data = await firstValueFrom(this.http.get<any>(`${this.apiBaseUrl}/by-thread/${encodeURIComponent(threadId)}`, { headers }))

      this.ngZone.run(() => {
        if (data.status === 'READY' || data.status === 'INITIALIZING') {
          this.callInfo = {
            callId: data.callId,
            accessKey: data.accessKey,
            status: 'CONNECTING',
            callUrl: null,
            startedAt: null,
            scheduledEndAt: null,
          }
          this.panelStatus = 'CONNECTING'
          this.changeDetector.detectChanges()

          this.pollStatus()
          this.startTimer()
          this.startPolling()
        } else if (data.status === 'NOT_ENABLED') {
          this.panelStatus = 'NOT_ENABLED'
          this.changeDetector.detectChanges()
          this.scheduleResolveRetry(threadId)
        } else if (data.status === 'SCHEDULED') {
          this.panelStatus = 'SCHEDULED'
          this.changeDetector.detectChanges()
          this.scheduleResolveRetry(threadId)
        } else {
          this.panelStatus = 'NO_CALL'
          this.changeDetector.detectChanges()
          this.scheduleResolveRetry(threadId)
        }
      })
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401 && !tokenRefreshed) {
        const token = await this.fetchSsoToken()
        if (token !== null) {
          this.ssoToken = token
          await this.resolveCall(threadId, true)
          return
        }
      }

      const reason = (error as { error?: { message?: string } })?.error?.message ?? null
      const status = reason === null ? 'NO_CALL' : (ACCESS_REASON_TO_STATUS[reason] ?? 'NO_CALL')
      this.ngZone.run(() => {
        this.panelStatus = status
        this.changeDetector.detectChanges()
      })
      this.scheduleResolveRetry(threadId)
    }
  }

  private formatDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
      .toString()
      .padStart(2, '0')
    const seconds = (totalSeconds % 60).toString().padStart(2, '0')
    return hours > 0 ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`
  }

  private formatMinutes(totalSeconds: number): string {
    const minutes = Math.ceil(totalSeconds / 60)
    return `${minutes} min`
  }

  private startTimer() {
    this.ngZone.runOutsideAngular(() => {
      this.timerInterval = setInterval(() => {
        const now = Date.now() + this.clockOffsetMs
        const elapsed = Math.floor((now - this.startTime) / 1000)
        this.elapsedTime = this.formatDuration(elapsed)

        if (this.callInfo !== null && this.callInfo.scheduledEndAt !== null) {
          const endTime = new Date(this.callInfo.scheduledEndAt).getTime()
          const remaining = Math.floor((endTime - now) / 1000)
          this.isOvertime = remaining <= 0
          this.remainingTime = remaining > 0 ? this.formatMinutes(remaining) : this.formatDuration(-remaining)
        } else {
          this.remainingTime = null
          this.isOvertime = false
        }

        this.changeDetector.detectChanges()
      }, 1000)
    })
  }

  private startPolling() {
    this.ngZone.runOutsideAngular(() => {
      this.pollInterval = setInterval(() => {
        this.pollStatus()
      }, 1000)
    })
  }

  private stopPolling() {
    if (this.pollInterval !== null) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
  }

  private async pollStatus() {
    if (this.callInfo === null) {
      return
    }

    try {
      const data = await firstValueFrom(
        this.http.get<any>(`${this.apiBaseUrl}/${this.callInfo.callId}/status`, {
          headers: { 'X-Call-Access-Key': this.callInfo.accessKey },
        })
      )

      this.ngZone.run(() => {
        this.pollFailureCount = 0
        this.isReconnecting = false
        const mappedStatus = this.mapSessionStatus(data.status)
        if (
          (this.isPausing && mappedStatus === 'PAUSED') ||
          (this.isResuming && mappedStatus === 'RECORDING') ||
          (this.isDisconnecting && mappedStatus === 'ENDED')
        ) {
          this.actionInProgress = false
          this.isPausing = false
          this.isResuming = false
          this.isDisconnecting = false
        }
        if (!this.actionInProgress) {
          this.panelStatus = mappedStatus
        }
        const callUrl = data.callUrl ?? null
        if (callUrl !== null) {
          this.callInfo!.callUrl = callUrl
        }
        const serverTime = data.serverTime ?? null
        if (serverTime !== null) {
          this.clockOffsetMs = new Date(serverTime).getTime() - Date.now()
        }
        const startedAt = data.startedAt ?? null
        if (startedAt !== null) {
          this.startTime = new Date(startedAt).getTime()
        }
        const scheduledEndAt = data.scheduledEndAt ?? null
        if (scheduledEndAt !== null) {
          this.callInfo!.scheduledEndAt = scheduledEndAt
        }
        this.canExtend = data.extendable === true
        this.canResend = data.resendable === true
        if (this.awaitingResend && mappedStatus !== 'ENDED') {
          // A new session has superseded the old ENDED one, so the replacement assistant is
          // joining. Drop back to the live view.
          this.awaitingResend = false
          this.resendSuccess = false
        }
        if (mappedStatus === 'ENDED') {
          if (this.awaitingResend && Date.now() < this.resendWaitUntil) {
            // Resend in flight: the new session hasn't superseded the old ENDED one yet.
            // Keep polling and hold the "on its way" screen instead of tearing down.
            this.changeDetector.detectChanges()
            return
          }
          this.awaitingResend = false
          this.actionInProgress = false
          this.isPausing = false
          this.isResuming = false
          this.isDisconnecting = false
          this.panelStatus = 'ENDED'
          this.clearTimers()
        }
        this.changeDetector.detectChanges()
      })
    } catch (error) {
      this.ngZone.run(() => this.handlePollFailure(error))
    }
  }

  private handlePollFailure(error: unknown) {
    const status = (error as { status?: number })?.status ?? null

    if (status === 401 && this.threadId !== null) {
      this.isReconnecting = true
      this.clearTimers()
      this.callInfo = null
      this.panelStatus = 'LOADING'
      this.actionInProgress = false
      this.isPausing = false
      this.isResuming = false
      this.isDisconnecting = false
      this.changeDetector.detectChanges()
      this.resolveCall(this.threadId)
      return
    }

    this.pollFailureCount += 1
    if (this.pollFailureCount >= this.reconnectAfterFailures && !this.isReconnecting) {
      this.isReconnecting = true
      this.changeDetector.detectChanges()
    }
  }

  private buildAuthHeaders(): Record<string, string> {
    if (this.callInfo === null) {
      throw new Error('No call info')
    }
    const headers: Record<string, string> = { 'X-Call-Access-Key': this.callInfo.accessKey }
    if (this.ssoToken !== null) {
      headers['Authorization'] = `Bearer ${this.ssoToken}`
    }
    return headers
  }

  private async sendPanelRequest(method: string, path: string) {
    const url = `${this.apiBaseUrl}/${path}`
    const headers = this.buildAuthHeaders()
    await firstValueFrom(method === 'POST' ? this.http.post(url, {}, { headers }) : this.http.get(url, { headers }))
  }
}
